/**
 * superadminDistributorService -- SuperAdmin distributor name search.
 *
 * Wraps GET /api/distributor/superadminList (SA-only, `superadmin.settings`),
 * the same list the Distributors page renders. `q` is a case-insensitive
 * substring match over the distributor name and the owner's name / email; the
 * server escapes LIKE wildcards and caps `limit` at 200. Disabled
 * distributors are excluded by the server's default.
 *
 * The caller owns request cancellation: pass an AbortSignal so a stale
 * response can be dropped when the user keeps typing.
 */
import axios from "@/axios";

export const DISTRIBUTOR_SEARCH_LIMIT = 20;

/**
 * Search distributors by name. Resolves to a normalized array:
 *   [{ id, name, plan, plan_id, plan_name, state, owner_email }]
 * `plan` is the ACTIVE plan's name (null unless the subscription is active).
 * `plan_id` / `plan_name` are the plan the subscription is PINNED to in any
 * state (a trial included), with `state` beside them; all three are null
 * when the distributor has no subscription.
 */
export async function searchDistributors(
    query,
    { signal, limit = DISTRIBUTOR_SEARCH_LIMIT } = {},
) {
    const resp = await axios.get("/api/distributor/superadminList", {
        params: { q: query, limit, order_by: "name", direction: "asc" },
        signal,
    });
    const rows = (resp && resp.data && resp.data.distributors) || [];
    return rows.map((d) => ({
        id: d.id,
        name: d.name,
        plan: d.plan || null,
        plan_id: d.plan_id ?? null,
        plan_name: d.plan_name || null,
        state: d.state || null,
        owner_email: (d.owner && d.owner.email) || null,
    }));
}
